"use client";

import { type ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import type { SelectList } from "../domain";
import { COLUMN_VALUE_FORMAT_OBJ } from "../domain";
import { isRecord } from "../app/is-record";
import { getColumnName } from "./column-helpers";
import { getNestedValue, setNestedValue } from "../app";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents, useTableConfig } from "./table-provider";


import { getHeaderText } from "./smart-table-shared";
export interface SmartEditModalProps<TData> {
  row: TData | null;
  columns: ColumnDef<TData>[];
  lists?: SelectList[];
  open: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (values: Partial<TData>) => void;
  /** Поле строки, значение которого показывается в заголовке окна; без него заголовка нет. */
  titleField?: string;
}

export function SmartEditModal<TData>({
  row,
  columns,
  lists = [],
  open,
  isSaving = false,
  onClose,
  onSave,
  titleField,
}: SmartEditModalProps<TData>) {
  const t = useTableLabelFn();
  const { Button, Select, Modal } = useTableComponents();
  const { locale } = useTableConfig();
  const [values, setValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (open) setValues({});
  }, [open]);

  const editableColumns = useMemo(
    () =>
      columns.filter((col) => {
        const meta = col.meta;
        return meta?.editable;
      }),
    [columns],
  );

  if (!row) return null;

  const rowRecord: Record<string, unknown> = isRecord(row) ? row : {};

  const getValue = (col: ColumnDef<TData>): unknown => {
    const colId = getColumnName(col);
    if (colId.includes(".")) {
      return getNestedValue(rowRecord, colId);
    }
    return rowRecord[colId];
  };

  const setValue = (col: ColumnDef<TData>, value: unknown) => {
    const colId = getColumnName(col);
    setValues((prev) => {
      if (colId.includes(".")) {
        const root = colId.split(".")[0]!;
        const rootValues: Record<string, unknown> = isRecord(prev[root])
          ? prev[root]
          : isRecord(rowRecord[root])
            ? rowRecord[root]
            : {};
        return {
          ...prev,
          [root]: setNestedValue(
            rootValues,
            colId.slice(colId.indexOf(".") + 1),
            value,
          ),
        };
      }
      return { ...prev, [colId]: value };
    });
  };

  const handleSave = () => {
    const updated: Partial<TData> = Object.assign<
      Partial<TData>,
      Record<string, unknown>
    >({}, values);
    onSave(updated);
  };

  const titleValue = titleField !== undefined ? rowRecord[titleField] : undefined;
  const title = typeof titleValue === "string" ? t("table.edit.title", { value: titleValue }) : undefined;

  return (
    <Modal
      open={open}
      onClose={() => !isSaving && onClose()}
      title={title}
    >
      <div className="space-y-3">
        {editableColumns.map((col) => {
          const colId = getColumnName(col);
          const meta = col.meta ?? {};
          const fmt = meta.format;
          const header = getHeaderText(col.header) ?? colId;
          const currentValue = getValue(col);
          const editedValue = colId.includes(".")
            ? getNestedValue(
                values[colId.split(".")[0]!],
                colId.slice(colId.indexOf(".") + 1),
              )
            : values[colId];
          const isEdited = editedValue !== undefined;

          const displayValue = isEdited ? editedValue : currentValue;
          const labelId = `edit-${colId.replace(/\./g, "-")}`;

          const list = lists.find((l) => l.propertyKey === colId);
          if (list) {
            const options = list.items.map((i) => ({
              value: i.value,
              label: i.label[locale] ?? i.value,
            }));
            return (
              <div key={colId} className="space-y-1">
                <label className="text-xs font-medium" htmlFor={labelId}>
                  {header} {t("table.editable")}
                </label>
                <Select
                  size="sm"
                  value={String(displayValue ?? "")}
                  onChange={(v) => setValue(col, v)}
                  ariaLabel={header}
                  className="w-full"
                  options={options}
                />
              </div>
            );
          }

          if (fmt === COLUMN_VALUE_FORMAT_OBJ.PERCENT) {
            const numValue =
              typeof displayValue === "number" ? displayValue * 100 : "";
            return (
              <div key={colId} className="space-y-1">
                <label className="text-xs font-medium" htmlFor={labelId}>
                  {header} {t("table.editable")}
                </label>
                <div className="relative">
                  <input
                    id={labelId}
                    type="number"
                    step="0.01"
                    value={numValue === "" ? "" : String(numValue)}
                    onChange={(e) =>
                      setValue(
                        col,
                        e.target.value === ""
                          ? null
                          : Number(e.target.value) / 100,
                      )
                    }
                    className="flex h-8 w-full rounded-md border border-border bg-background px-2 pr-6 text-sm"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-text-secondary">
                    %
                  </span>
                </div>
              </div>
            );
          }

          if (fmt === COLUMN_VALUE_FORMAT_OBJ.CURRENCY) {
            return (
              <div key={colId} className="space-y-1">
                <label className="text-xs font-medium" htmlFor={labelId}>
                  {header} {t("table.editable")}
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-text-secondary">
                    $
                  </span>
                  <input
                    id={labelId}
                    type="number"
                    step="0.01"
                    value={displayValue == null ? "" : String(displayValue)}
                    onChange={(e) =>
                      setValue(
                        col,
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    className="flex h-8 w-full rounded-md border border-border bg-background px-2 pl-6 text-sm"
                  />
                </div>
              </div>
            );
          }

          // number, text, date, default → plain input
          const inputType =
            fmt === COLUMN_VALUE_FORMAT_OBJ.DATE
              ? "date"
              : fmt === COLUMN_VALUE_FORMAT_OBJ.NUMBER
                ? "number"
                : "text";
          return (
            <div key={colId} className="space-y-1">
              <label className="text-xs font-medium" htmlFor={labelId}>
                {header} {t("table.editable")}
              </label>
              <input
                id={labelId}
                type={inputType}
                step={
                  fmt === COLUMN_VALUE_FORMAT_OBJ.NUMBER ? "0.01" : undefined
                }
                value={displayValue == null ? "" : String(displayValue)}
                onChange={(e) =>
                  setValue(
                    col,
                    fmt === COLUMN_VALUE_FORMAT_OBJ.NUMBER
                      ? e.target.value === ""
                        ? null
                        : Number(e.target.value)
                      : e.target.value,
                  )
                }
                className="flex h-8 w-full rounded-md border border-border bg-background px-2 text-sm"
              />
            </div>
          );
        })}
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
